import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

export default function Highway() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);
    const t2 = setTimeout(() => setPhase(2), 2000);
    const t3 = setTimeout(() => setPhase(3), 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const steps = [
    { label: 'CRYPTOGRAPHIC_IGNITION', color: 'text-cyan-400' },
    { label: 'SHADOW_SYNC_ESTABLISHED', color: 'text-amber-400' },
    { label: 'SOVEREIGNTY_VERIFIED', color: 'text-fuchsia-400' },
    { label: 'HIGHWAY_ACCESS_GRANTED', color: 'text-emerald-400' },
  ];

  return (
    <div className="min-h-screen pt-24 flex flex-col items-center justify-center relative overflow-hidden bg-zinc-950">
      <div
        className="absolute bottom-0 left-0 right-0 h-[70vh] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(6,182,212,0.15) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(6,182,212,0.15) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          transform: 'perspective(400px) rotateX(65deg)',
          transformOrigin: 'bottom',
          animation: 'road-scroll 1.5s linear infinite',
        }}
      />
      <div className="absolute top-1/2 left-0 w-full h-[1px] bg-cyan-500/60 box-glow-cyan" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[200px] bg-cyan-500/20 blur-[100px] pointer-events-none" />

      <div className="relative z-10 text-center px-6 max-w-3xl">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-zinc-100 mb-4"
        >
          The Highway is <span className="text-amber-400 text-glow-amber">Live</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-zinc-400 font-mono text-xs md:text-sm max-w-xl mx-auto mb-10"
        >
          Local-first execution. Immutable audit trails. Zero telemetry.
          <br />Your vehicle has entered the sovereign grid.
        </motion.p>

        {/* Boot Sequence */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-6 backdrop-blur-sm mb-8 text-left font-mono text-xs">
          <div className="flex items-center gap-2 text-zinc-500 mb-4 border-b border-zinc-800 pb-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
            <span className="tracking-widest uppercase">boot_sequence.log</span>
          </div>
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: phase >= i ? 1 : 0.2, x: 0 }}
              transition={{ delay: i * 0.4, duration: 0.4 }}
              className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0"
            >
              <span className="text-zinc-500">[{String(i + 1).padStart(2, '0')}]</span>
              <span className={phase >= i ? step.color : 'text-zinc-700'}>{step.label}</span>
              <span className={phase >= i ? 'text-emerald-400' : 'text-zinc-700'}>
                {phase >= i ? '✓ OK' : '...'}
              </span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 20 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/showroom"
            className="px-8 py-3 bg-zinc-900 border border-zinc-700 text-zinc-100 font-bold uppercase tracking-widest text-sm rounded hover:border-cyan-500 hover:text-cyan-400 transition-all inline-flex items-center gap-3"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} /> Build Another
          </Link>
          <Link
            to="/manifesto"
            className="px-8 py-3 bg-amber-500/10 border border-amber-500/50 text-amber-400 font-bold uppercase tracking-widest text-sm rounded hover:bg-amber-500/20 transition-all inline-flex items-center gap-3"
          >
            <SafeIcon icon={FiIcons.FiBookOpen} /> Read Manifesto
          </Link>
        </motion.div>

        {phase >= 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-xs text-amber-400 italic text-glow-amber"
          >
            Keep the faith, the good exists.
          </motion.div>
        )}
      </div>
    </div>
  );
}